/** Server-orchestrated read with no collection to project (ADR-0019). A check is not a hold. */
import { getTRPCClient } from "@repo/api";
import type { CheckOrderSetBookingParams } from "@repo/api/schemas";
import { useEffect, useState } from "react";

export type BookingCheckInput = CheckOrderSetBookingParams;
type Reply = Awaited<ReturnType<ReturnType<typeof getTRPCClient>["booking"]["checkOrderSetBooking"]["query"]>>;
export type BookingCheckResult = Extract<Reply, { success: true }>["data"];
type Answer = { status: "checked"; check: BookingCheckResult } | { status: "error"; message: string };

export function useCheckOrderSetBooking(input: BookingCheckInput | null, revision = 0) {
  const [answer, setAnswer] = useState<{ input: BookingCheckInput; revision: number; answer: Answer } | null>(null);
  useEffect(() => {
    if (!input) return;
    let current = true;
    const timer = setTimeout(async () => {
      try {
        const reply = await getTRPCClient().booking.checkOrderSetBooking.query(input);
        if (current)
          setAnswer({
            input,
            revision,
            answer: reply.success
              ? { status: "checked", check: reply.data }
              : { status: "error", message: "Couldn't check availability. Try again." },
          });
      } catch {
        // Domain errors are toasted by the global tRPC error link.
        if (current)
          setAnswer({
            input,
            revision,
            answer: {
              status: "error",
              message: "Availability could not be checked. Review the proposal and try again.",
            },
          });
      }
    }, 300);
    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [input, revision]);
  if (!input) return { status: "idle" } as const;
  if (answer?.input !== input || answer.revision !== revision) return { status: "checking" } as const;
  return answer.answer;
}
